import { cn } from 'lib/utils'
import { GalleryHorizontalEnd, Image as ImageIcon, Video, Wand2 } from 'lucide-react'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

// Linked to the store's ContentShape so the literals can't silently drift.
export type IntentShape = Extract<
  import('../store/idea-store').ContentShape,
  'static_image' | 'carousel' | 'short_vertical_video'
>
export const SHAPE_META: Record<IntentShape, { label: string; Icon: typeof ImageIcon }> = {
  static_image: { label: 'Image', Icon: ImageIcon },
  carousel: { label: 'Carousel', Icon: GalleryHorizontalEnd },
  short_vertical_video: { label: 'Reel', Icon: Video },
}

/**
 * Segmented format control with a sliding acid thumb, shared between the Home
 * hero (CreationHero) and the calendar's day-tap creation popover. Auto is a
 * first-class option (Taka decides).
 *
 * The hero drives the full choreography via `inferred` / `resolved` /
 * `thinking`: bare "Auto" on an empty card, shimmering while the card thinks,
 * then resolving to the inferred shape as part of the reveal. Consumers that
 * skip those props get the plain uncoupled variant: a bare Auto that never
 * shimmers or grows a suffix.
 */
/** Tooltip for every segment while the format is locked to the uploads. */
const LOCKED_TITLE = 'As-is posts take their format from your uploads.'

export function ShapeSlider({
  value,
  inferred = 'static_image',
  resolved = false,
  thinking = false,
  disabled = false,
  isMobile = false,
  lockedShape = null,
  hideAuto = false,
  onChange,
}: {
  value: IntentShape | null
  /** Taka's live pick while Auto is selected (hero only). */
  inferred?: IntentShape
  /** True once the card has content to infer from; before that Auto stays bare. */
  resolved?: boolean
  thinking?: boolean
  disabled?: boolean
  isMobile?: boolean
  /**
   * Bring-your-own-content: the format IS the uploads (1 video → Reel,
   * 1 image → Image, 2+ → Carousel), so the control still tells the user
   * what they're posting but nothing is choosable — the derived segment
   * reads selected, every segment disables, and the tooltip explains why.
   */
  lockedShape?: IntentShape | null
  /**
   * Drop the Auto segment (Post-your-own room: Auto is a generation concept;
   * there the format is either the user's declared intent or the media's
   * fact). With hideAuto, a null value means NO selection — no thumb.
   */
  hideAuto?: boolean
  onChange: (next: IntentShape | null) => void
}) {
  const locked = lockedShape !== null
  const effectiveValue = locked ? lockedShape : value
  const showInferred = resolved && !thinking && !isMobile && !locked && !hideAuto
  // The one composition point for the Auto suffix: the option label (thumb
  // re-measure identity) and the rendered span must never drift apart.
  const autoSuffix = showInferred ? ` · ${SHAPE_META[inferred].label}` : ''
  // The deciding animation only plays while Taka's pick is live: with an
  // explicit override selected the inferred format can't apply, so a shimmer
  // would narrate a decision that never lands.
  const deciding = thinking && resolved && value === null && !locked
  const options = useMemo(
    () => [
      ...(hideAuto
        ? []
        : [
            {
              key: 'auto',
              value: null as IntentShape | null,
              label: `Auto${autoSuffix}`,
              Icon: Wand2,
              title: 'Taka picks the format for you.',
            },
          ]),
      ...(['static_image', 'carousel', 'short_vertical_video'] as IntentShape[]).map(shape => ({
        key: shape,
        value: shape as IntentShape | null,
        label: SHAPE_META[shape].label,
        Icon: SHAPE_META[shape].Icon,
        title: `Switch to ${SHAPE_META[shape].label}.`,
      })),
    ],
    [autoSuffix, hideAuto]
  )
  // Without Auto, null means "nothing declared yet": no active segment, no
  // thumb. With Auto, null IS the Auto segment (index 0 fallback as before).
  const rawIdx = options.findIndex(o => o.value === effectiveValue)
  const activeIdx = hideAuto ? rawIdx : Math.max(0, rawIdx)
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null)
  // Labels change width (Auto shows the inferred shape), so re-measure on any
  // input that can move the active segment, and on resize (font reflow).
  useLayoutEffect(() => {
    const measure = () => {
      if (activeIdx < 0) {
        // Nothing declared (hideAuto + null): no thumb at all — stale
        // geometry from a previous selection must not linger.
        setThumb(null)
        return
      }
      const el = buttonRefs.current[activeIdx]
      if (el) {
        const { offsetLeft: left, offsetWidth: width } = el
        // Bail on identical geometry so identity-only options churn (e.g. the
        // inferred shape flipping while its suffix is hidden) skips a render.
        setThumb(prev =>
          prev && prev.left === left && prev.width === width ? prev : { left, width }
        )
      }
    }
    measure()
    window.addEventListener('resize', measure)
    // A late webfont swap reflows label widths with no resize event.
    void document.fonts?.ready.then(measure).catch(() => undefined)
    return () => window.removeEventListener('resize', measure)
  }, [activeIdx, options])

  return (
    <div
      role="group"
      aria-label="Post format"
      title={locked ? LOCKED_TITLE : undefined}
      className="relative flex flex-shrink-0 items-center rounded-full border border-line bg-bg/60 p-0.5"
    >
      {thumb && (
        <span
          aria-hidden="true"
          className="absolute inset-y-0.5 rounded-full border border-acid/60 bg-acid/15 transition-all duration-200 ease-out"
          style={{ left: thumb.left, width: thumb.width }}
        />
      )}
      {options.map((opt, i) => {
        const selected = i === activeIdx
        const isAuto = opt.value === null
        return (
          <button
            key={opt.key}
            ref={el => {
              buttonRefs.current[i] = el
            }}
            type="button"
            disabled={disabled || locked}
            aria-pressed={selected}
            title={locked ? LOCKED_TITLE : opt.title}
            aria-label={
              locked
                ? selected
                  ? `${opt.label}. ${LOCKED_TITLE}`
                  : `${opt.label} unavailable. ${LOCKED_TITLE}`
                : isAuto
                  ? `Auto: Taka ${deciding ? 'is picking' : 'picks'} the format.${
                      resolved && !thinking ? ` Currently ${SHAPE_META[inferred].label}.` : ''
                    }`
                  : `Switch to ${opt.label}.`
            }
            onClick={() => onChange(opt.value)}
            className={cn(
              'relative z-[1] flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium transition-colors',
              // 28x20 chips were sub-32px tap targets on touch (monkey A11Y).
              isMobile && 'min-h-[32px]',
              selected ? 'text-paper' : 'text-paper-faint hover:text-paper',
              locked && !selected && 'opacity-45 hover:text-paper-faint'
            )}
          >
            <opt.Icon
              size={12}
              className={cn('flex-shrink-0', isAuto && deciding && 'animate-pulse')}
            />
            {/* Mobile keeps the control compact: only the active segment
                spells its label, the rest stay icons. */}
            {(!isMobile || selected) &&
              (isAuto ? (
                <span className="flex items-center whitespace-nowrap">
                  {/* The shimmer marks Taka deciding; the resolved shape then
                      slides in beside "Auto" as the payoff of that beat. */}
                  <span className={cn(deciding && 'shimmer-text')}>Auto</span>
                  {autoSuffix && (
                    <span
                      key={autoSuffix}
                      className="animate-in whitespace-pre duration-300 fade-in slide-in-from-left-1 motion-reduce:animate-none"
                    >
                      {autoSuffix}
                    </span>
                  )}
                </span>
              ) : (
                <span className="whitespace-nowrap">{opt.label}</span>
              ))}
          </button>
        )
      })}
    </div>
  )
}
